import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Icon from "./icons";
import Style from "./appShell.module.scss";
import {
  logout as curatorLogout,
  selectinfo as selectCurator,
} from "../../store/features/curatorSlice";
import {
  logout as bidderLogout,
  selectBidderProfile,
} from "../../store/features/bidderSlice";

// Who is looking decides what the rail offers. "public" is the unauthenticated
// player list, which gets the top bar and no rail at all.
const NAV = {
  curator: [
    { text: "Auction Room", link: "/curator/auction", icon: "live" },
    { text: "Player List", link: "/curator/playerlist", icon: "list" },
    { text: "Teams", link: "/curator/teamlist", icon: "teams" },
  ],
  bidder: [
    { text: "Bidding", link: "/bidder/bidding", icon: "live" },
    { text: "My Squad", link: "/bidder/teamview", icon: "squad" },
    { text: "Auction Pool", link: "/bidder/allplayers", icon: "pool" },
  ],
  public: [],
};

// Six tints, picked off the name so a person or team keeps the same colour
// everywhere without one being stored against them.
const TINTS = ["#0040a8", "#363b64", "#1b7f4b", "#b4661e", "#6f48eb", "#c32f27"];

export function tintFor(name) {
  const key = String(name || "");
  let sum = 0;
  for (let i = 0; i < key.length; ++i) sum += key.charCodeAt(i);
  return TINTS[sum % TINTS.length];
}

// `label` names the page next to the wordmark. Signed-in screens are named by
// the role, but there is more than one public page now, so they say which.
export default function AppShell({
  children,
  role = "public",
  bare,
  aside,
  label,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const whoRef = useRef(null);

  const curator = useSelector(selectCurator);
  const bidder = useSelector(selectBidderProfile);
  const profile = role === "bidder" ? bidder : role === "curator" ? curator : {};

  const name = profile?.name || (role === "public" ? "Guest" : "Account");
  const initial = String(name).charAt(0).toUpperCase();
  const items = NAV[role] || [];

  // The auction room is nested under /curator/auction, and the active player
  // list lives under it, so the longest matching link wins rather than the
  // first one that happens to be a prefix.
  const activeLink = items
    .map((i) => i.link)
    .filter((link) => location.pathname.toLowerCase().startsWith(link.toLowerCase()))
    .sort((a, b) => b.length - a.length)[0];

  const onLogout = async () => {
    setMenuOpen(false);
    await dispatch(role === "bidder" ? bidderLogout() : curatorLogout());
    window.location.href = "/";
  };

  // The account menu is the only way out on a phone, where the name and the
  // log out button beside it are hidden for room. It closes the way a menu is
  // expected to: a tap anywhere else, Escape, or moving to another page.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (!whoRef.current?.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className={Style.shell}>
      {items.length > 0 && (
        <nav className={Style.rail}>
          <Link to={items[0].link} className={Style.mark} title="Auction">
            A
          </Link>

          {items.map((item) => (
            <Link
              key={item.link}
              to={item.link}
              className={`${Style.railItem} ${
                item.link === activeLink ? Style.active : ""
              }`}
            >
              <Icon name={item.icon} />
              <span className={Style.railTip}>{item.text}</span>
            </Link>
          ))}

          <div className={Style.railSpacer} />

          <div
            className={Style.railAvatar}
            style={{ backgroundColor: tintFor(name) }}
          >
            {initial}
          </div>
        </nav>
      )}

      <div className={Style.stack}>
        <header className={Style.topbar}>
          <div className={Style.brand}>
            <span className={Style.wordmark}>Auction</span>
            <span className={Style.role}>
              {role === "public" ? label || "Player List" : role}
            </span>
          </div>

          <div className={Style.topRight}>
            {aside}

            {role !== "public" && (
              <>
                <div className={Style.topDivider} />
                <div className={Style.who} ref={whoRef}>
                  <div className={Style.whoText}>
                    <div className={Style.whoName}>{name}</div>
                  </div>

                  <button
                    type="button"
                    className={Style.whoAvatar}
                    style={{ backgroundColor: tintFor(name) }}
                    onClick={() => setMenuOpen((open) => !open)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label={`Account menu for ${name}`}
                  >
                    {initial}
                  </button>

                  {menuOpen && (
                    <div className={Style.whoMenu} role="menu">
                      <div className={Style.whoMenuName}>{name}</div>
                      <button
                        type="button"
                        role="menuitem"
                        className={Style.logout}
                        onClick={onLogout}
                      >
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </header>

        {/* Keyed on the route so navigating remounts this subtree and the enter
            animation replays. The rail and top bar sit outside it and stay
            put, so only what actually changed moves. `bare` lets a page lay
            out its own surfaces instead of one white panel. */}
        <div key={location.pathname} className="au-page">
          {bare ? children : <main className={Style.panel}>{children}</main>}
        </div>
      </div>
    </div>
  );
}
